import React from "react";
import {
  Alert,
  Box,
  Button,
  ButtonProps,
  FormField,
  Header,
  Popover,
  Select,
  SelectProps,
  SpaceBetween,
  StatusIndicator,
} from "@amzn/awsui-components-react";
import { OmsExportPreset } from "../../lusca-search-api/generated-src";
import { OmsPresetsState, presetErrorMessage } from "../../hooks/useOmsPresets";
import { presetScopeLabel } from "./omsPresetEditing";

export default function OMSPresetToolbar({
  state,
  applied,
  disabled,
  saveDisabled = disabled,
  onApply,
  onSave,
  onReview,
  onManage,
  onUndoPreset,
  saveRef,
  reviewRef,
  selectRef,
  manageRef,
  modified = false,
  onCustom,
  editing = false,
}: {
  state: OmsPresetsState;
  applied?: OmsExportPreset;
  disabled: boolean;
  saveDisabled?: boolean;
  onApply: (presetId: string) => void;
  onSave: () => void;
  onReview: () => void;
  onManage: () => void;
  onUndoPreset?: () => void;
  saveRef: React.RefObject<ButtonProps.Ref>;
  reviewRef: React.RefObject<ButtonProps.Ref>;
  selectRef: React.RefObject<SelectProps.Ref>;
  manageRef: React.RefObject<ButtonProps.Ref>;
  modified?: boolean;
  onCustom: () => void;
  editing?: boolean;
}) {
  const busy = disabled || state.read.isLoading;
  return (
    <section className="oms-preset-section" aria-label="Export preset">
      <SpaceBetween size="s">
        <Header
          variant="h3"
          info={
            <Popover
              header="About presets"
              dismissAriaLabel="Close preset help"
              size="medium"
              triggerType="custom"
              content={
                <SpaceBetween size="s">
                  <Box>
                    Save row filters, column selections, or both for reuse with
                    another report for this product. Presets are private to you
                    and save settings, not report data.
                  </Box>
                  <Box>
                    Changing export settings does not update a saved preset
                    until you confirm the changes.
                  </Box>
                </SpaceBetween>
              }
            >
              <Button variant="inline-link" ariaLabel="About presets">
                Info
              </Button>
            </Popover>
          }
          actions={
            <Button ref={manageRef} disabled={busy} onClick={onManage}>
              Manage presets
            </Button>
          }
        >
          {editing ? "Editing preset" : "Preset"}
        </Header>
        <div className="oms-preset-toolbar">
          <FormField label="Saved preset (optional)" stretch>
            <Select
              expandToViewport
              ref={selectRef}
              ariaLabel="Saved preset"
              placeholder="Choose a preset"
              filteringType="auto"
              filteringPlaceholder="Search presets"
              filteringAriaLabel="Search presets"
              filteringClearAriaLabel="Clear preset search"
              noMatch="No matching presets."
              statusType={
                state.list.isLoading || state.read.isLoading
                  ? "loading"
                  : "finished"
              }
              loadingText={
                state.read.isLoading ? "Applying preset" : "Loading presets"
              }
              disabled={busy || editing}
              selectedOption={
                applied
                  ? {
                      value: applied.presetId,
                      label: applied.name,
                      description: presetScopeLabel[applied.scope],
                    }
                  : null
              }
              options={[
                ...(applied && onUndoPreset
                  ? [
                      {
                        value: "undo",
                        label: "Undo applying preset",
                        description: `Return to the settings used before ${applied.name}.`,
                      },
                    ]
                  : []),
                {
                  value: "custom",
                  label: "No preset",
                  description: "Keep the current rows and columns.",
                },
                ...(state.list.data || []).map((preset) => ({
                  value: preset.presetId,
                  label: preset.name,
                  description: presetScopeLabel[preset.scope],
                })),
              ]}
              onChange={({ detail }) => {
                if (detail.selectedOption.value === "undo") onUndoPreset?.();
                else if (detail.selectedOption.value === "custom") onCustom();
                else onApply(detail.selectedOption.value as string);
              }}
            />
          </FormField>
          <div className="oms-preset-toolbar__actions">
            {applied && (modified || editing) && (
              <Button
                ref={reviewRef}
                disabled={busy || saveDisabled}
                onClick={onReview}
              >
                Review preset changes
              </Button>
            )}
            <Button
              ref={saveRef}
              disabled={busy || saveDisabled}
              onClick={onSave}
            >
              Save as new preset
            </Button>
          </div>
        </div>
        {!applied &&
          !state.list.isLoading &&
          !state.list.isError &&
          state.list.data?.length === 0 && (
            <Box color="text-body-secondary">
              No presets yet. Configure your rows and columns, then choose{" "}
              <Box variant="span" fontWeight="bold">
                Save as new preset
              </Box>{" "}
              to reuse those settings.
            </Box>
          )}
        {applied && (
          <div className="oms-preset-section__status">
            <div className="oms-preset-context__status">
              <StatusIndicator type={modified ? "warning" : "success"}>
                {modified ? "Unsaved preset changes" : "Preset applied"}
              </StatusIndicator>
            </div>
          </div>
        )}
        {state.list.isError && (
          <Alert
            type="warning"
            header="Presets unavailable"
            action={
              <Button
                onClick={() => {
                  state.list.refetch().catch(() => undefined);
                }}
              >
                Reload presets
              </Button>
            }
          >
            You can still configure and create an export.
          </Alert>
        )}
        {state.read.isError && (
          <Alert
            type="error"
            header="Could not apply preset"
            action={
              <Button
                onClick={() => {
                  if (state.read.variables) onApply(state.read.variables);
                }}
              >
                Apply preset again
              </Button>
            }
          >
            {presetErrorMessage(state.read.error)}
          </Alert>
        )}
      </SpaceBetween>
    </section>
  );
}
